import { TERMINAL_SOCKET_CLOSE_CODES, type ClientMessage, type PlayerInput, type ServerMessage } from '../shared/protocol.js';
import { PROTOCOL_VERSION } from '../shared/protocol-version.js';
import { RaceFrameDecoder } from '../shared/snapshot-codec.js';
import type { PlayerIdentity, RacerSnapshot } from '../shared/types.js';
import {
  parseServerMessage,
  ProtocolMismatchError,
} from '../shared/server-message-schema.js';
import type { RaceDiagnostics } from './race-diagnostics.js';

export interface RaceConnectionOptions {
  roomId: string;
  player: PlayerIdentity;
  onMessage(message: ServerMessage): void;
  onStatus(status: ConnectionStatus): void;
  onLatency?(latencyMs: number): void;
  onTerminalClose?(code: number, reason: string): void;
  onProtocolMismatch?(): void;
  onClientError?(error: Error): void;
  diagnostics?: RaceDiagnostics;
}

export interface SpectatorConnectionOptions {
  roomId: string;
  /** Empty only when requesting a public room. */
  inviteToken: string;
  onMessage(message: ServerMessage): void;
  onStatus(status: ConnectionStatus): void;
  onLatency?(latencyMs: number): void;
  onTerminalClose?(code: number, reason: string): void;
  onProtocolMismatch?(): void;
  onClientError?(error: Error): void;
  diagnostics?: RaceDiagnostics;
}

export type ConnectionStatus = 'connecting' | 'open' | 'reconnecting' | 'closed' | 'local';

export interface RaceTransport {
  send(message: ClientMessage): void;
  sendInput(input: PlayerInput): void;
  setSnapshotInterval?(intervalMs: number): void;
  /** Available only on the in-browser authoritative simulation. */
  setPaused?(paused: boolean): void;
  /** Bounded local command journal, when the transport can replay its engine. */
  exportReplay?(): readonly import('./local-race-replay.js').LocalRaceReplayEvent[];
  close(): void;
}

const MAX_BUFFERED_BYTES = 48 * 1_024;
const STALLED_AFTER_MS = 6_000;
const MAX_CONSECUTIVE_INVALID_MESSAGES = 3;

function livenessNow(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

export class RaceConnection implements RaceTransport {
  private socket: WebSocket | null = null;
  private closed = false;
  private closedStatusReported = false;
  private protocolMismatchHandled = false;
  private clientFailureHandled = false;
  private reconnectAttempt = 0;
  private reconnectTimer: number | undefined;
  private pingTimer: number | undefined;
  private watchdogTimer: number | undefined;
  private lastInboundAt = livenessNow();
  private consecutiveInvalidMessages = 0;
  private lastRoundTripMs: number | null = null;
  private pendingInput: PlayerInput | null = null;
  private readonly pendingControl = new Map<ClientMessage['type'], ClientMessage>();
  private readonly racerMetadata = new Map<string, RacerSnapshot>();
  private readonly frameDecoder = new RaceFrameDecoder();

  constructor(private readonly options: RaceConnectionOptions) {
    this.options.diagnostics?.record('transport', 'connection-created', {
      roomId: options.roomId,
      role: 'driver',
    });
    window.addEventListener('online', this.reconnectNow);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.connect();
  }

  send(message: ClientMessage): void {
    if (message.type === 'input') {
      this.sendInput(message.input);
      return;
    }
    if (this.sendImmediately(message)) return;
    if (message.type !== 'ping') this.pendingControl.set(message.type, message);
    if (message.type !== 'ping') {
      this.options.diagnostics?.record('transport', 'command-buffered', { type: message.type });
    }
  }

  sendInput(input: PlayerInput): void {
    // Keep only the newest control state. Replaying a backlog after a mobile
    // radio stall makes the car feel frozen long after connectivity returns.
    // Reset, fire and Heat Run banking are edge-like actions, though: a newer
    // neutral sample must not erase them before the socket accepts the action.
    this.pendingInput = coalescePendingInput(this.pendingInput, input);
    this.flushPending();
  }

  close(): void {
    if (this.closed) return;
    const socket = this.socket;
    this.socket = null;
    this.markClosed();
    this.reportClosed();
    socket?.close(1000, 'Client closed');
  }

  private connect(): void {
    if (this.closed) return;
    if (this.reconnectTimer !== undefined) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.options.onStatus(this.reconnectAttempt === 0 ? 'connecting' : 'reconnecting');
    this.options.diagnostics?.record('transport', 'connect-attempt', {
      attempt: this.reconnectAttempt,
      role: 'driver',
    });
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const query = new URLSearchParams({ v: String(PROTOCOL_VERSION) });
    const socket = new WebSocket(`${protocol}//${window.location.host}/ws?${query}`);
    this.socket = socket;
    socket.addEventListener('open', () => {
      if (this.socket !== socket) return;
      this.reconnectAttempt = 0;
      this.lastInboundAt = livenessNow();
      this.consecutiveInvalidMessages = 0;
      socket.send(JSON.stringify({
        type: 'authenticate',
        roomId: this.options.roomId,
        playerId: this.options.player.id,
        token: this.options.player.token,
      }));
      this.options.onStatus('open');
      this.options.diagnostics?.record('transport', 'socket-open', { role: 'driver' });
      this.startPinging();
      this.startWatchdog();
      this.flushPending();
    });
    socket.addEventListener('message', (event) => {
      if (this.socket !== socket) return;
      let delivered: ServerMessage;
      try {
        const message = parseServerMessage(event.data);
        if (message.type === 'welcome') {
          if (message.protocolVersion !== PROTOCOL_VERSION) {
            this.protocolMismatch(socket);
            return;
          }
          this.racerMetadata.clear();
          for (const racer of message.racers) this.racerMetadata.set(racer.id, racer);
          this.frameDecoder.seed(message.racers);
        } else if (message.type === 'player-joined') {
          this.racerMetadata.set(message.racer.id, message.racer);
        }
        if (message.type === 'frame' || message.type === 'frame-delta') {
          if (message.v !== PROTOCOL_VERSION) {
            this.protocolMismatch(socket);
            return;
          }
          const snapshot = this.frameDecoder.decode(message, this.racerMetadata);
          for (const racer of snapshot.racers) this.racerMetadata.set(racer.id, racer);
          this.acceptInboundMessage();
          this.flushPending();
          delivered = snapshot;
        } else {
          this.acceptInboundMessage();
          this.flushPending();
          delivered = message;
        }
      } catch (error) {
        if (error instanceof ProtocolMismatchError) {
          this.protocolMismatch(socket);
          return;
        }
        this.rejectInboundMessage(socket);
        return;
      }
      this.deliverInboundMessage(delivered, socket);
    });
    socket.addEventListener('close', (event) => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.stopPinging();
      this.stopWatchdog();
      this.options.diagnostics?.record('transport', 'socket-close', {
        role: 'driver', code: event.code, reason: event.reason,
      });
      if (event.code === 4002) {
        this.protocolMismatch();
        return;
      }
      if (this.closed) {
        this.reportClosed();
        return;
      }
      if (TERMINAL_SOCKET_CLOSE_CODES.has(event.code)) {
        this.markClosed();
        this.reportClosed();
        this.options.onTerminalClose?.(event.code, event.reason);
        return;
      }
      this.scheduleReconnect();
    });
    socket.addEventListener('error', () => socket.close());
  }

  private scheduleReconnect(): void {
    this.reconnectAttempt += 1;
    this.options.onStatus('reconnecting');
    const baseDelay = Math.min(8_000, 500 * 2 ** Math.min(this.reconnectAttempt, 4));
    const delay = Math.round(baseDelay * (0.86 + Math.random() * 0.28));
    this.options.diagnostics?.record('transport', 'reconnect-scheduled', {
      attempt: this.reconnectAttempt, delay,
    });
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = undefined;
      this.connect();
    }, delay);
  }

  private readonly reconnectNow = (): void => {
    if (this.closed || this.socket?.readyState === WebSocket.OPEN || this.socket?.readyState === WebSocket.CONNECTING) return;
    if (this.reconnectTimer !== undefined) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.connect();
  };

  private readonly onVisibilityChange = (): void => {
    if (!document.hidden && navigator.onLine !== false) this.reconnectNow();
  };

  private startPinging(): void {
    this.stopPinging();
    const ping = (): void => this.send({
      type: 'ping',
      sentAt: Date.now(),
      ...(this.lastRoundTripMs !== null ? { rtt: this.lastRoundTripMs } : {}),
    });
    ping();
    this.pingTimer = window.setInterval(ping, 2_000);
  }

  private stopPinging(): void {
    if (this.pingTimer !== undefined) window.clearInterval(this.pingTimer);
    this.pingTimer = undefined;
  }

  private sendImmediately(message: ClientMessage): boolean {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN || socket.bufferedAmount > MAX_BUFFERED_BYTES) return false;
    socket.send(JSON.stringify(message));
    return true;
  }

  private flushPending(): void {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN || socket.bufferedAmount > MAX_BUFFERED_BYTES) return;
    for (const [type, message] of this.pendingControl) {
      if (!this.sendImmediately(message)) return;
      this.pendingControl.delete(type);
    }
    if (!this.pendingInput || socket.bufferedAmount > MAX_BUFFERED_BYTES) return;
    const latest = this.pendingInput;
    if (this.sendImmediately({ type: 'input', input: latest })) this.pendingInput = null;
  }

  private startWatchdog(): void {
    this.stopWatchdog();
    this.watchdogTimer = window.setInterval(() => {
      const socket = this.socket;
      if (!socket || socket.readyState !== WebSocket.OPEN) return;
      if (livenessNow() - this.lastInboundAt <= STALLED_AFTER_MS) {
        this.flushPending();
        return;
      }
      // A half-open mobile WebSocket can remain OPEN indefinitely. Detach it
      // locally and reconnect immediately while prediction keeps rendering.
      this.socket = null;
      this.stopPinging();
      this.stopWatchdog();
      socket.close(4000, 'Connection stalled');
      this.options.diagnostics?.record('transport', 'watchdog-stall', { role: 'driver' });
      this.scheduleReconnect();
    }, 1_000);
  }

  private stopWatchdog(): void {
    if (this.watchdogTimer !== undefined) window.clearInterval(this.watchdogTimer);
    this.watchdogTimer = undefined;
  }

  private acceptInboundMessage(): void {
    this.lastInboundAt = livenessNow();
    this.consecutiveInvalidMessages = 0;
  }

  private rejectInboundMessage(socket: WebSocket): void {
    this.consecutiveInvalidMessages += 1;
    this.options.diagnostics?.record('transport', 'invalid-inbound', {
      role: 'driver', consecutive: this.consecutiveInvalidMessages,
    });
    if (
      this.consecutiveInvalidMessages < MAX_CONSECUTIVE_INVALID_MESSAGES
      || this.socket !== socket
    ) return;
    this.socket = null;
    this.stopPinging();
    this.stopWatchdog();
    socket.close(4000, 'Invalid server data');
    this.scheduleReconnect();
  }

  private deliverInboundMessage(message: ServerMessage, socket: WebSocket): void {
    try {
      if (message.type === 'pong') {
        const roundTrip = Math.max(0, Date.now() - message.sentAt);
        this.lastRoundTripMs = roundTrip;
        this.options.onLatency?.(roundTrip);
      }
      this.options.onMessage(message);
    } catch (error) {
      this.failClientMessageHandler(socket, error);
    }
  }

  private failClientMessageHandler(socket: WebSocket, error: unknown): void {
    if (this.clientFailureHandled) return;
    this.clientFailureHandled = true;
    if (this.socket === socket) this.socket = null;
    this.markClosed();
    this.reportClosed();
    const failure = error instanceof Error ? error : new Error('The race client could not process server data');
    this.options.diagnostics?.record('transport', 'client-handler-failed', failure);
    try {
      if (this.options.onClientError) this.options.onClientError(failure);
      else console.error('[network] client message handler failed', failure);
    } catch (reportingError) {
      console.error('[network] client error reporting failed', reportingError);
    }
    socket.close(1011, 'Client message handler failed');
  }

  private markClosed(): void {
    this.closed = true;
    window.removeEventListener('online', this.reconnectNow);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.reconnectTimer !== undefined) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.stopPinging();
    this.stopWatchdog();
  }

  private reportClosed(): void {
    if (this.closedStatusReported) return;
    this.closedStatusReported = true;
    this.options.onStatus('closed');
  }

  private protocolMismatch(socket?: WebSocket): void {
    if (this.protocolMismatchHandled) return;
    this.protocolMismatchHandled = true;
    this.options.diagnostics?.record('transport', 'protocol-mismatch', { role: 'driver' });
    this.markClosed();
    this.reportClosed();
    this.options.onProtocolMismatch?.();
    socket?.close(4002, 'Client update required');
  }
}

export function coalescePendingInput(previous: PlayerInput | null, latest: PlayerInput): PlayerInput {
  const sameAttempt = previous?.raceAttempt === latest.raceAttempt;
  return {
    ...latest,
    fire: latest.fire || sameAttempt && previous?.fire === true,
    reset: latest.reset || sameAttempt && previous?.reset === true,
    bank: latest.bank || sameAttempt && previous?.bank === true,
  };
}

/** Read-only live transport used by invite holders after a race has started. */
export class SpectatorConnection implements RaceTransport {
  private socket: WebSocket | null = null;
  private closed = false;
  private closedStatusReported = false;
  private protocolMismatchHandled = false;
  private clientFailureHandled = false;
  private reconnectAttempt = 0;
  private reconnectTimer: number | undefined;
  private pingTimer: number | undefined;
  private watchdogTimer: number | undefined;
  private lastInboundAt = livenessNow();
  private consecutiveInvalidMessages = 0;
  private readonly racerMetadata = new Map<string, RacerSnapshot>();
  private readonly frameDecoder = new RaceFrameDecoder();

  constructor(private readonly options: SpectatorConnectionOptions) {
    this.options.diagnostics?.record('transport', 'connection-created', {
      roomId: options.roomId,
      role: 'spectator',
    });
    window.addEventListener('online', this.reconnectNow);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.connect();
  }

  send(message: ClientMessage): void {
    if (message.type !== 'ping') return;
    const socket = this.socket;
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
  }

  sendInput(_input: PlayerInput): void {}

  close(): void {
    if (this.closed) return;
    const socket = this.socket;
    this.socket = null;
    this.markClosed();
    this.reportClosed();
    socket?.close(1000, 'Spectator closed');
  }

  private connect(): void {
    if (this.closed) return;
    if (this.reconnectTimer !== undefined) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.options.onStatus(this.reconnectAttempt === 0 ? 'connecting' : 'reconnecting');
    this.options.diagnostics?.record('transport', 'connect-attempt', {
      attempt: this.reconnectAttempt,
      role: 'spectator',
    });
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const query = new URLSearchParams({ v: String(PROTOCOL_VERSION) });
    const socket = new WebSocket(`${protocol}//${window.location.host}/ws?${query}`);
    this.socket = socket;
    socket.addEventListener('open', () => {
      if (this.socket !== socket) return;
      this.reconnectAttempt = 0;
      this.lastInboundAt = livenessNow();
      this.consecutiveInvalidMessages = 0;
      socket.send(JSON.stringify({
        type: 'spectate',
        roomId: this.options.roomId,
        inviteToken: this.options.inviteToken,
      }));
      this.options.onStatus('open');
      this.options.diagnostics?.record('transport', 'socket-open', { role: 'spectator' });
      this.startTimers();
    });
    socket.addEventListener('message', (event) => {
      if (this.socket !== socket) return;
      let delivered: ServerMessage;
      try {
        const message = parseServerMessage(event.data);
        if (message.type === 'spectator-welcome') {
          if (message.protocolVersion !== PROTOCOL_VERSION) {
            this.protocolMismatch(socket);
            return;
          }
          this.racerMetadata.clear();
          for (const racer of message.racers) this.racerMetadata.set(racer.id, racer);
          this.frameDecoder.seed(message.racers);
        } else if (message.type === 'player-joined') {
          this.racerMetadata.set(message.racer.id, message.racer);
        }
        if (message.type === 'frame' || message.type === 'frame-delta') {
          if (message.v !== PROTOCOL_VERSION) {
            this.protocolMismatch(socket);
            return;
          }
          const snapshot = this.frameDecoder.decode(message, this.racerMetadata);
          for (const racer of snapshot.racers) this.racerMetadata.set(racer.id, racer);
          this.acceptInboundMessage();
          delivered = snapshot;
        } else {
          this.acceptInboundMessage();
          delivered = message;
        }
      } catch (error) {
        if (error instanceof ProtocolMismatchError) {
          this.protocolMismatch(socket);
          return;
        }
        this.rejectInboundMessage(socket);
        return;
      }
      this.deliverInboundMessage(delivered, socket);
    });
    socket.addEventListener('close', (event) => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.stopTimers();
      this.options.diagnostics?.record('transport', 'socket-close', {
        role: 'spectator', code: event.code, reason: event.reason,
      });
      if (event.code === 4002) {
        this.protocolMismatch();
        return;
      }
      if (this.closed) {
        this.reportClosed();
        return;
      }
      if (TERMINAL_SOCKET_CLOSE_CODES.has(event.code)) {
        this.markClosed();
        this.reportClosed();
        this.options.onTerminalClose?.(event.code, event.reason);
        return;
      }
      this.scheduleReconnect();
    });
    socket.addEventListener('error', () => socket.close());
  }

  private scheduleReconnect(): void {
    this.reconnectAttempt += 1;
    this.options.onStatus('reconnecting');
    const baseDelay = Math.min(8_000, 500 * 2 ** Math.min(this.reconnectAttempt, 4));
    const delay = Math.round(baseDelay * (0.86 + Math.random() * 0.28));
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = undefined;
      this.connect();
    }, delay);
  }

  private readonly reconnectNow = (): void => {
    if (this.closed || this.socket?.readyState === WebSocket.OPEN || this.socket?.readyState === WebSocket.CONNECTING) return;
    if (this.reconnectTimer !== undefined) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.connect();
  };

  private readonly onVisibilityChange = (): void => {
    if (!document.hidden && navigator.onLine !== false) this.reconnectNow();
  };

  private startTimers(): void {
    this.stopTimers();
    const ping = (): void => this.send({ type: 'ping', sentAt: Date.now() });
    ping();
    this.pingTimer = window.setInterval(ping, 2_000);
    this.watchdogTimer = window.setInterval(() => {
      const socket = this.socket;
      if (!socket || socket.readyState !== WebSocket.OPEN || livenessNow() - this.lastInboundAt <= STALLED_AFTER_MS) return;
      this.socket = null;
      this.stopTimers();
      socket.close(4000, 'Connection stalled');
      this.scheduleReconnect();
    }, 1_000);
  }

  private stopTimers(): void {
    if (this.pingTimer !== undefined) window.clearInterval(this.pingTimer);
    if (this.watchdogTimer !== undefined) window.clearInterval(this.watchdogTimer);
    this.pingTimer = undefined;
    this.watchdogTimer = undefined;
  }

  private acceptInboundMessage(): void {
    this.lastInboundAt = livenessNow();
    this.consecutiveInvalidMessages = 0;
  }

  private rejectInboundMessage(socket: WebSocket): void {
    this.consecutiveInvalidMessages += 1;
    this.options.diagnostics?.record('transport', 'invalid-inbound', {
      role: 'spectator', consecutive: this.consecutiveInvalidMessages,
    });
    if (
      this.consecutiveInvalidMessages < MAX_CONSECUTIVE_INVALID_MESSAGES
      || this.socket !== socket
    ) return;
    this.socket = null;
    this.stopTimers();
    socket.close(4000, 'Invalid server data');
    this.scheduleReconnect();
  }

  private deliverInboundMessage(message: ServerMessage, socket: WebSocket): void {
    try {
      if (message.type === 'pong') this.options.onLatency?.(Math.max(0, Date.now() - message.sentAt));
      this.options.onMessage(message);
    } catch (error) {
      this.failClientMessageHandler(socket, error);
    }
  }

  private failClientMessageHandler(socket: WebSocket, error: unknown): void {
    if (this.clientFailureHandled) return;
    this.clientFailureHandled = true;
    if (this.socket === socket) this.socket = null;
    this.markClosed();
    this.reportClosed();
    const failure = error instanceof Error ? error : new Error('The spectator client could not process server data');
    this.options.diagnostics?.record('transport', 'client-handler-failed', failure);
    try {
      if (this.options.onClientError) this.options.onClientError(failure);
      else console.error('[network] spectator message handler failed', failure);
    } catch (reportingError) {
      console.error('[network] spectator error reporting failed', reportingError);
    }
    socket.close(1011, 'Client message handler failed');
  }

  private markClosed(): void {
    this.closed = true;
    window.removeEventListener('online', this.reconnectNow);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.reconnectTimer !== undefined) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.stopTimers();
  }

  private reportClosed(): void {
    if (this.closedStatusReported) return;
    this.closedStatusReported = true;
    this.options.onStatus('closed');
  }

  private protocolMismatch(socket?: WebSocket): void {
    if (this.protocolMismatchHandled) return;
    this.protocolMismatchHandled = true;
    this.options.diagnostics?.record('transport', 'protocol-mismatch', { role: 'spectator' });
    this.markClosed();
    this.reportClosed();
    this.options.onProtocolMismatch?.();
    socket?.close(4002, 'Client update required');
  }
}
